import React, {
    useRef,
    useState,
    useCallback,
    useEffect,
    useMemo,
} from 'react';
import { cn } from '@rocco/utils/cn';
import { SearchLine } from '@archipro-design/icons';

const DEFAULT_ZOOM = 1.25;
const DEFAULT_LENS_SIZE = 150;

interface CircleMagnifierProps {
    /** Image URL used for the magnified view inside the lens. */
    src: string;
    /** Alt text for the magnified image. */
    alt?: string;
    /**
     * Natural aspect ratio of the image (width / height).
     * Used to compute the visible image area within the container
     * so the lens clips at the actual image edges.
     */
    imageAspectRatio?: number;
    /** Magnification level applied inside the lens (default: 1.25). */
    zoom?: number;
    /** Diameter of the circular lens in pixels (default: 150). */
    lensSize?: number;
    /** Additional CSS classes for the outer wrapper. */
    className?: string;
    /** The base image content to overlay the magnifier on. */
    children: React.ReactNode;
}

/**
 * Wraps its children with a circular magnifier lens that follows the cursor
 * on hover. The lens shows a zoomed-in portion of the supplied `src` image,
 * rendered with `object-fit: contain` so it matches the base image layout
 * regardless of aspect ratio. The lens clips naturally at the actual image
 * edges (computed from `imageAspectRatio`) rather than the full container.
 */
const CircleMagnifier = ({
    src,
    alt = '',
    imageAspectRatio,
    zoom = DEFAULT_ZOOM,
    lensSize = DEFAULT_LENS_SIZE,
    className,
    children,
}: CircleMagnifierProps) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const [isHover, setIsHover] = useState(false);
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const [bounds, setBounds] = useState({ w: 1, h: 1 });
    const [imageLoaded, setImageLoaded] = useState(false);
    const [isOverInteractive, setIsOverInteractive] = useState(false);
    const [isUpscaling, setIsUpscaling] = useState(false);

    useEffect(() => {
        setImageLoaded(false);
    }, [src]);

    const lensRadius = lensSize / 2;

    /**
     * The visible image content area within the wrapper, computed as if
     * the image is rendered with object-fit: contain and centered.
     * Falls back to the full wrapper bounds when no aspect ratio is given.
     */
    const clipArea = useMemo(() => {
        if (!imageAspectRatio || imageAspectRatio <= 0) {
            return { x: 0, y: 0, w: bounds.w, h: bounds.h };
        }

        const containerAR = bounds.w / bounds.h;

        if (imageAspectRatio >= containerAR) {
            const imgH = bounds.w / imageAspectRatio;
            return {
                x: 0,
                y: (bounds.h - imgH) / 2,
                w: bounds.w,
                h: imgH,
            };
        }

        const imgW = bounds.h * imageAspectRatio;
        return {
            x: (bounds.w - imgW) / 2,
            y: 0,
            w: imgW,
            h: bounds.h,
        };
    }, [bounds.w, bounds.h, imageAspectRatio]);

    const isCursorOverImage =
        pos.x >= clipArea.x &&
        pos.x <= clipArea.x + clipArea.w &&
        pos.y >= clipArea.y &&
        pos.y <= clipArea.y + clipArea.h;

    const showLens =
        isHover &&
        isCursorOverImage &&
        imageLoaded &&
        !isOverInteractive &&
        !isUpscaling;

    const handleMouseEnter = useCallback(() => {
        if (!wrapRef.current) return;
        const rect = wrapRef.current.getBoundingClientRect();
        setBounds({ w: rect.width, h: rect.height });
        setIsHover(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsHover(false);
    }, []);

    const handleMouseMove = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (!wrapRef.current) return;
            const target = e.target as HTMLElement;
            setIsOverInteractive(
                !!target.closest('button, a, [data-no-magnifier]'),
            );
            setIsUpscaling(!!target.closest('[data-upscaling]'));
            const rect = wrapRef.current.getBoundingClientRect();
            setPos({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            });
            if (
                Math.abs(bounds.w - rect.width) > 0.5 ||
                Math.abs(bounds.h - rect.height) > 0.5
            ) {
                setBounds({ w: rect.width, h: rect.height });
            }
        },
        [bounds.w, bounds.h],
    );

    return (
        <div
            ref={wrapRef}
            className={cn(
                'relative',
                isUpscaling ? 'cursor-wait' : showLens && 'cursor-none',
                className,
            )}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleMouseMove}
        >
            {children}

            {/* Clip region matching the visible image content area */}
            <div
                aria-hidden
                className="pointer-events-none absolute overflow-hidden"
                style={{
                    left: clipArea.x,
                    top: clipArea.y,
                    width: clipArea.w,
                    height: clipArea.h,
                }}
            >
                {/* Magnifier lens -- follows cursor, clips at image edges */}
                <div
                    className={cn(
                        'absolute z-10 overflow-hidden rounded-full',
                        'border border-white/25 shadow-[0_8px_30px_rgba(0,0,0,0.4)]',
                        'ring-1 ring-black/20',
                        'transition-opacity duration-150',
                        showLens ? 'opacity-100' : 'opacity-0',
                    )}
                    style={{
                        width: lensSize,
                        height: lensSize,
                        left: pos.x - clipArea.x - lensRadius,
                        top: pos.y - clipArea.y - lensRadius,
                    }}
                >
                    <img
                        src={src}
                        alt={alt}
                        draggable={false}
                        onLoad={() => setImageLoaded(true)}
                        className="pointer-events-none max-w-none select-none object-contain"
                        style={{
                            position: 'absolute',
                            width: bounds.w,
                            height: bounds.h,
                            left: lensRadius - pos.x,
                            top: lensRadius - pos.y,
                            transform: `scale(${zoom})`,
                            transformOrigin: `${pos.x}px ${pos.y}px`,
                        }}
                    />

                    {/* Magnifying glass icon at the lens centre */}
                    <SearchLine
                        className="absolute left-1/2 top-1/2 z-20 h-5 w-5 -translate-x-1/2 -translate-y-1/2 drop-shadow-md"
                        style={{ fill: 'rgba(255, 255, 255, 0.6)' }}
                    />
                </div>
            </div>
        </div>
    );
};

export default CircleMagnifier;
